<script>
	import thumb_MxMDYnZxp94 from '$lib/Components/Youtube/images/MxMDYnZxp94.jpg';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import houseProtectedAgainstNoiseImg from './images/house-protected-against-noise.jpg';
	import lawEnforcementNeighborsNoiseImg from './images/law-enforcement-neighbors-noise.jpg';
	import neighborPlayingMusicImg from './images/neighbor-playing-music.png';
	import resourcesSoundproofingImg from './images/resources-soundproofing.png';
</script>

<Section>
	<p>
		One morning you sit down to study, read, or spend some relaxing time in your
		own home when all of a sudden loud music disturbs you. Neighbors playing
		loud music during the day can be an incredibly disturbing event,
		particularly when you can't escape the noise and are not sure where to turn
		to for help. In this blog, we will review the most common situations so you
		can quickly create an action plan for getting the noise turned down.
	</p>
	<Image
		src={houseProtectedAgainstNoiseImg}
		alt="outside of house with sound waves to represent neighbors playing loud music during the day"
	/>
	<nav class="flex flex-col items-center gap-8">
		<h2>What best describes your situation?</h2>
		<ul class="list-none text-center">
			<li>
				<a href="#enforcement">I want law enforcement to stop the loud noise.</a
				>
			</li>
			<li>
				<a href="#landlord">I want my landlord to stop the loud noise.</a>
			</li>
			<li>
				<a href="#communication"
					>I want my neighbor to understand my pain & stop playing music /
					making noise.</a
				>
			</li>
			<li>
				<a href="#space"
					>I'm taking this into my own hands (or) the city and my neighbor
					aren't listening to me. I need to soundproof my space!</a
				>
			</li>
		</ul>
	</nav>
</Section>

<Section id="enforcement">
	<h2>I want law enforcement to stop the loud noise</h2>
	<p>
		Each city has a noise ordinance that sets specific rules for when loud
		noises may be played and what is considered an acceptable range of loudness
		(dB). If a violation is reported, police may come to the scene, or a noise
		officer may come out with a decibel reader to confirm if the sound is
		outside acceptable limits.
	</p>
	<p>
		Generally, these rules are not going to help you during the day unless the
		sound is incredibly excessive (as in hearing damage level). From around 7am
		– 10pm, your neighbor will not be in violation if the noise is below 60 dB –
		the equivalent of a vacuum cleaner. But the decibel reading will be from
		your property line. A great check is to try turning on a vacuum cleaner to
		see if it drowns out the noise. If you can still hear the music, you should
		file a noise complaint.
	</p>
	<Image
		src={lawEnforcementNeighborsNoiseImg}
		alt="law enforcement vehicle that may show up due to noise complaints"
	/>
	<p>
		Check your local noise ordinance by typing “[city name] noise ordinance”
		into search to get the times and decibel ratings. They will also provide
		specific instructions on how to file the complaint.
	</p>
	<p>
		If you already filed a complaint and it is not working, you can either
		soundproof your own home and/or consider small claims court, where a lawyer
		is not required. You can either sue your neighbor for the cost of
		soundproofing your home, or general damages.
	</p>
	<Bubble>
		<h3 class="!text-indow-grey-dark font-bold capitalize">
			You will need to prove:
		</h3>
		<ul>
			<li>Evidence of excessive and disturbing noise.</li>
			<li>The person you are suing is the one creating the noise.</li>
			<li>Your daily life in your home is impacted.</li>
			<li>
				You have taken action in communicating with your neighbor or the noise
				ordinance board and they have ignored your requests.
			</li>
		</ul>

		<h3 class="!text-indow-grey-dark font-bold capitalize">
			Evidence you should consider collecting:
		</h3>
		<ul>
			<li>Police or noise ordinance reports.</li>
			<li>Video and/or audio recording.</li>
			<li>Text messages, emails, or written letters with your neighbor.</li>
		</ul>
	</Bubble>
	<p>
		Small claims courts have judgments between $2,500 and $7,500. A request of
		$20/day for your suffering is reasonable, but if the noise is bad enough it
		impacts your work and daily life, requesting the cost of soundproofing
		materials may be required.
	</p>
</Section>

<Section id="landlord">
	<h2>I want my landlord to stop the loud noise</h2>
	<p>
		If you are renting, you have the right to a quiet space and can ask your
		landlord to investigate with the city or speak directly to the neighbor. The
		source of the noise does not have to be in the same apartment building for
		your landlord to step in. If your neighbor also rents, they may be in
		violation of their rental agreement and can be evicted if the noise does not
		stop.
	</p>
	<p>
		Adding another person to the mix can elongate the time that you are
		experiencing the noise. Make sure to read our sections on communicating with
		your neighbor directly or contacting law enforcement before proceeding.
	</p>
</Section>

<Section id="communication">
	<h2>
		I want my neighbor to understand my pain & stop playing music / making noise
	</h2>
	<p>
		It's hard to imagine, but your neighbor has their own emotional needs for
		playing this loud music. We don't know what is going on in their lives. They
		might be trying to make a living as a musician or creating art for self
		expression. Having a frame of mind of understanding instead of only anger
		will give you a higher chance of amenable conversation instead of a heated
		argument.
	</p>
	<Image
		src={neighborPlayingMusicImg}
		alt="man looking upset while neighbor plays saxophone outside window"
	/>
	<Bubble>
		{#snippet titleSlot()}
			<h3>Here are some tips for starting a conversation:</h3>
		{/snippet}
		<ul>
			<li>
				Take a deep breath before saying anything and calm yourself down. Remain
				polite and respectful, but be firm in what you need.
			</li>
			<li>
				Start with explaining how the music is impacting you (ex. I work at home
				and can't concentrate) and ask if they could turn it down.
			</li>
			<li>
				If you do not have their phone number, try to get it so you can text
				them for both of your convenience in the future.
			</li>
			<li>
				Be clear in how you will escalate the matter if it does not seem like
				they are taking you seriously (ex. call their landlord or file a police
				report).
			</li>
			<li>
				Write down the date, time, and details of your conversation in case this
				continues in the future and you need it for reference.
			</li>
		</ul>
	</Bubble>
	<p>
		Starting with a conversation directly to the source is always the easiest
		and quickest way for you to get relief. You may have to try this a few times
		for it to properly work. Are there compromises you can offer on hours that
		you really don't want the noise and other hours where they might be able to
		play their music?
	</p>
	<p>
		Consider talking to your other neighbors to see if it is also an issue with
		them and if you can work together to communicate that change is needed for
		the neighborhood. If this does not work, contact law enforcement or
		soundproof your space from the noise.
	</p>
</Section>

<Section id="space">
	<h2>
		I'm taking this into my own hands (or) the city and my neighbor aren't
		listening to me. I need to soundproof my space!
	</h2>
	<p>
		It is possible to create a quiet zone without changing the behaviors of
		external things that you simply can't control. This will help you attain a
		more comfortable space to reduce your stress and get better sleep,
		contributing to a healthier life.
	</p>
	<p>
		First, decide if you need soundproofing or sound absorbing treatments. Noise
		travels through vibration, which is carried through the air. So to stop
		noise, you must control the airflow. Noise generated outside, like neighbors
		playing loud music, calls for <a
			href="/resources/blog/soundproof-windows-home-depot"
			>soundproofing products</a
		>. They stop noise from traveling through windows, ceilings, doors, and
		walls. Here are some examples:
	</p>
	<h3>Soundproof Window Inserts</h3>
	<p>
		Installing window inserts is an easy and effective way of soundproofing
		windows. Indow® Acoustic Grade window inserts can reduce up to 70% of
		outside noise when placed over single-pane windows and up to 50% of noise
		coming through double-pane windows. In an office or home, soundproofing
		windows is easy with our inserts that press into your existing window
		frame—no hassle, nails, or adhesives required. Check out this video of a
		customer who is a drummer and was dealing with complaints from the neighbors
		for playing loud music during the day. After installing Indow inserts, his
		neighbors were satisfied with the noise levels.
	</p>
	<!-- <Button href="/instant-estimate">TRY OUR INSERT PRICING CALCULATOR</Button> -->
	<Youtube
		posterImageSrc={thumb_MxMDYnZxp94}
		src="https://www.youtube.com/embed/MxMDYnZxp94"
		title="New way to reduce noise in your home studio - Indow customer"
	/>
	<h3>Acoustic Caulk</h3>
	<p>
		Look through your space and find areas where you see gaps between things
		like your door frame and the wall. While it seems like a small area that
		might be insignificant, sealing up all gaps will help reduce the noise.
		Consider it like a mission to find a leak in the bathtub. The leak still
		happens with even a pinprick. Standard caulks will work, but <a
			href="https://www.greengluecompany.com/products/noiseproofing-sealant"
			>Green Glue sealant</a
		> has a specialty formula for acoustic damping.
	</p>
	<p>
		Noise generated inside your home requires sound absorption products, which
		reduce echo. If you are in an apartment building with noise coming through
		the walls, this will help control the sound.
	</p>
	<h3>Acoustic panels</h3>
	<p>
		When sound enters a room, it can bounce off various surfaces and continue
		amplifying in your space. Plush rugs with sound dampening rug pads, wall
		hangings, upholstered furniture and plants will help absorb sound. If you
		want to get really advanced, consider placing acoustic panels on the wall
		where sound is being transmitted and/or the wall opposite to the incoming
		noise.
	</p>
	<Image
		src={resourcesSoundproofingImg}
		alt="woman suffering from loud music during the day"
		class="w-48"
	/>
	<p>
		While you may be fantasizing about pranks you can play on your neighbor as
		payback for the stress you are receiving, we applaud you on your efforts to
		find a more peaceful solution. It may be difficult, but finding compromise
		to create a healthy space for you both, and a peaceful neighborhood for all,
		will give you the biggest win in the long run.
	</p>
</Section>

<GetAFreeEstimate />
<Reviews backgroundColor="white" />
